import { Injectable } from "@angular/core";
import { AuthConfigurationService } from "./auth/auth-configuration.service";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { WebApiConfigurationService } from "src/app/shared/configuration/web-api/web-api-configuration.service";
import { UserInfoConfigurationService } from "./user-info/user-info-configuration.service";
import { QuickLinkConfigurationService } from "./quick-links/quick-link-configuration.service";
import { ConfigurationModule } from "./configuration.module";
import { Configuration } from "./configuration";
import { UserInfoConfiguration } from "./user-info/user-info-configuration";
import { OpenIdConfigurationService } from "./open-id/open-id-configuration.service";
import { AuthorityStorageService } from "./auth/authority-storage.service";

@Injectable({
  providedIn: ConfigurationModule,
})
export class ConfigurationService {
  
  get configuration(): Configuration {
    return {
      endSessionUrl:this.endSessionUrl,
      auth: this.authenticationConfiguration.configuration,
      disabledTabTemplates: this.disabledTabTemplates,
      featureTrackingEnabled: this.featureTrackingEnabled,
      name: this.name,
      selectCloud: this.selectCloud,
      webApi: this.webApiConfigurationService.configurations,
      userInfo: this.userInfoConfigurationService.configuration,
      quicklinks: this.quicklLinksConfigurationService.configuration,
      openIdConfiguration : this.openIdConfigurationService.configuration
    };
  }

  loaded;

  name;
  endSessionUrl;
  featureTrackingEnabled;
  selectCloud;

  disabledTabTemplates;

  get websiterUrl() {
    return window.location.origin;
  }

  constructor(
    private http: HttpClient,
    private authenticationConfiguration: AuthConfigurationService,
    private webApiConfigurationService: WebApiConfigurationService,
    private userInfoConfigurationService: UserInfoConfigurationService,
    private quicklLinksConfigurationService: QuickLinkConfigurationService,
    private openIdConfigurationService: OpenIdConfigurationService,
    private authorityStorageService: AuthorityStorageService,
  
  ) {}

  readConfigurations() {
    return this.http
      .get<Configuration[]>("/deployments/configurations.json")
      .toPromise();
  }
  
  readOpenidConfiguration(url)
  {
    return this.http
    .get<any>(url)
    .toPromise();
  }
  
  async getAuthority(authorityUrl: string): Promise<void> {
    try {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'OpenGroundCloud':'U3VwZXJCYXRtYW5GYXN0'
      });
      
      const authority = await this.http.get<any>(authorityUrl,{headers}).toPromise();
      this.authorityStorageService.storeAuthority(authority);
    } catch (error) {
      console.error('Error fetching authority:', error);
      throw error;
    }
  }

  async loadCurrentConfiguration() {
    try {
      // Read configurations and determine current configuration
      const configurations = await this.readConfigurations();
      const currentConfiguration = await this.workOutCurrentConfiguration(configurations);
  
      // Check if configuration exists
      if (!currentConfiguration) {
        throw new Error("No configuration for this site");
      }
  
      // Construct URL for authority
      const url = `${currentConfiguration.webApi[0].protocol}://${currentConfiguration.webApi[0].domain}/api/v1.1/identity/authority`;
  
      // Fetch authority information
      await this.getAuthority(url);
  
      // Fetch OpenID configuration using the loaded authority
      const config = await this.readOpenidConfiguration(
        this.authorityStorageService.loadAuthority() + "/.well-known/openid-configuration"
      );
  
      // Process user info configuration
      let userInfo: UserInfoConfiguration = {
        enabled: true,
        url: config.userinfo_endpoint,
      };
  
      // Set end session URL and assign user info to current configuration
      this.endSessionUrl = config.end_session_endpoint;
      currentConfiguration.userInfo = userInfo;
  
      // Load and return final configuration
      const configuration = this.loadConfiguration(currentConfiguration);
      this.loaded = true;
  
      return configuration;
    } catch (error) {
      console.error("Error loading configuration:", error);
      throw error; // Propagate the error upwards
    }
  }

  loadConfiguration(configuration) {
    this.name = "openground";
    this.featureTrackingEnabled = true;
    this.disabledTabTemplates = [];
    this.selectCloud = true;
    this.loadWebApiConfigurations(configuration.webApi);
    this.loadAuthenticationConfiguration(configuration.authentication);
    this.loadUserInfoConfiguration(configuration.userInfo);
    this.loadQuickLinksConfiguration(configuration.quickLinks);
    return this.configuration;
  }

  loadQuickLinksConfiguration(configuration) {
    return this.quicklLinksConfigurationService.loadConfiguration(
      configuration
    );
  }

  loadAuthenticationConfiguration(authenticationConfigurations) {
    this.authenticationConfiguration.loadConfiguration(
      authenticationConfigurations
    );
    this.authenticationConfiguration.loaded = true;
  }

  loadWebApiConfigurations(webApiConfigurations) {
    this.webApiConfigurationService.loadConfigurations(webApiConfigurations);
  }

  loadUserInfoConfiguration(configuration) {
    return this.userInfoConfigurationService.loadConfiguration(configuration);
  }

  workOutCurrentConfiguration(configurations) {
    let currentConfig: Configuration = null;
    for (const configuration of configurations) {
      const siteMatch = configuration.siteMatch;

      if (siteMatch) {
        const regex = new RegExp(siteMatch);
        const match = this.websiterUrl.match(regex);
        if (
          match &&
          match.length &&
          match[0].length === this.websiterUrl.length
        ) {
          currentConfig = configuration;
        }
      }
    }

    return currentConfig;
  }
}
